var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { action, observable, runInAction, makeObservable } from 'mobx';
import { API_PATH } from '../../constants';
import { getAxios } from '../../backend';
import { EventName, } from '@whatsper/texterchat-common';
/** Maps a partial quick-reply update into the patch operations the API expects. */
export function buildQuickReplyPatchOps(updates) {
    const operations = [];
    if (updates.send) {
        if (updates.send.contents !== undefined) {
            operations.push({ updateSendContents: updates.send.contents });
        }
        if (updates.send.target !== undefined) {
            operations.push({ updateSendTarget: updates.send.target });
        }
        if (updates.send.betweenMessagesDelay !== undefined) {
            operations.push({ updateBetweenMessagesDelay: updates.send.betweenMessagesDelay });
        }
    }
    if (updates.display) {
        if (updates.display.position !== undefined) {
            operations.push({ updateDisplayPosition: updates.display.position });
        }
        if (updates.display.title !== undefined) {
            operations.push({ updateDisplayTitle: updates.display.title });
        }
        if (updates.display.note !== undefined) {
            operations.push({ updateDisplayNote: updates.display.note });
        }
        if (updates.display.departments !== undefined) {
            operations.push({ updateDisplayDepartments: updates.display.departments });
        }
        if (updates.display.users !== undefined) {
            operations.push({ updateDisplayUsers: updates.display.users });
        }
    }
    if (updates.clientMetadata !== undefined) {
        operations.push({ updateClientMetadata: updates.clientMetadata });
    }
    return operations;
}
class QuickRepliesStore {
    constructor(rootStore) {
        Object.defineProperty(this, "rootStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "quickReplies", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: observable.array([])
        });
        Object.defineProperty(this, "total", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "quickRepliesLoading", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "quickRepliesLoadError", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "quickRepliesFetched", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        /** Whether the standalone quick-reply settings modal (opened from general settings) is open. */
        Object.defineProperty(this, "isSettingsModalOpen", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        /** Channel the settings modal is filtered by (quick replies are configured per channel). */
        Object.defineProperty(this, "selectedChannel", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "setSelectedChannel", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (channel) => {
                this.selectedChannel = channel;
            }
        });
        Object.defineProperty(this, "ensureDefaultChannel", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                if (this.selectedChannel)
                    return;
                const { defaultAccount, accounts } = this.rootStore.channels;
                const account = defaultAccount || accounts[0];
                if (account) {
                    this.selectedChannel = { name: account.channel, id: account.accountId };
                }
            }
        });
        Object.defineProperty(this, "openSettingsModal", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.ensureDefaultChannel();
                if (!this.quickRepliesFetched) {
                    this.loadQuickReplies();
                }
                this.isSettingsModalOpen = true;
            }
        });
        Object.defineProperty(this, "closeSettingsModal", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.isSettingsModalOpen = false;
            }
        });
        Object.defineProperty(this, "upsertQuickReply", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (reply) => {
                const existingIndex = this.quickReplies.findIndex((existingReply) => existingReply._id === reply._id);
                if (existingIndex === -1) {
                    this.quickReplies.unshift(reply);
                    this.total += 1;
                }
                else {
                    this.quickReplies.splice(existingIndex, 1, reply);
                }
            }
        });
        Object.defineProperty(this, "removeQuickReply", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (id) => {
                const nextReplies = this.quickReplies.filter((reply) => reply._id !== id);
                if (nextReplies.length !== this.quickReplies.length) {
                    this.quickReplies.replace(nextReplies);
                    this.total = Math.max(0, this.total - 1);
                }
            }
        });
        Object.defineProperty(this, "loadQuickReplies", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (query) => __awaiter(this, void 0, void 0, function* () {
                runInAction(() => {
                    this.quickRepliesLoading = true;
                    this.quickRepliesLoadError = false;
                });
                try {
                    const { data } = yield getAxios().then((axios) => {
                        return axios.get(API_PATH.QUICK_REPLIES, {
                            params: query,
                        });
                    });
                    runInAction(() => {
                        this.quickReplies.replace(data.quickReplies);
                        this.total = data.total;
                        this.quickRepliesFetched = true;
                    });
                }
                catch (error) {
                    console.error('Error loading quick replies', error);
                    runInAction(() => {
                        this.quickRepliesLoadError = true;
                    });
                }
                finally {
                    runInAction(() => {
                        this.quickRepliesLoading = false;
                    });
                }
            })
        });
        Object.defineProperty(this, "addQuickReply", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (reply) => __awaiter(this, void 0, void 0, function* () {
                const axios = yield getAxios();
                const { data } = yield axios.post(API_PATH.QUICK_REPLIES, reply);
                if (!(data === null || data === void 0 ? void 0 : data._id)) {
                    yield this.loadQuickReplies();
                    return data;
                }
                this.upsertQuickReply(data);
                return data;
            })
        });
        /**
         * Edit a quick reply by ID. Accepts partial updates for send, display, and/or clientMetadata.
         */
        Object.defineProperty(this, "editQuickReply", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (id, updates) => __awaiter(this, void 0, void 0, function* () {
                const operations = buildQuickReplyPatchOps(updates);
                if (operations.length === 0) {
                    console.warn('editQuickReply called with no updates');
                    return;
                }
                const axios = yield getAxios();
                const { data } = yield axios.patch(`${API_PATH.QUICK_REPLIES}/${id}`, operations);
                if (!(data === null || data === void 0 ? void 0 : data._id)) {
                    yield this.loadQuickReplies();
                    return data;
                }
                this.upsertQuickReply(data);
                return data;
            })
        });
        Object.defineProperty(this, "deleteQuickReply", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (id) => __awaiter(this, void 0, void 0, function* () {
                const axios = yield getAxios();
                yield axios.delete(`${API_PATH.QUICK_REPLIES}/${id}`);
                this.removeQuickReply(id);
            })
        });
        Object.defineProperty(this, "onQuickReplyCreatedEvent", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (event) => __awaiter(this, void 0, void 0, function* () {
                try {
                    this.upsertQuickReply(event.quickReply);
                }
                catch (error) {
                    console.error(`Error adding new quick reply ${event.quickReply._id}`, error);
                }
            })
        });
        makeObservable(this);
        this.rootStore = rootStore;
        this.rootStore.events
            .addListener(EventName.quick_reply_created, (data) => this.onQuickReplyCreatedEvent(data))
            .addListener(EventName.quick_reply_deleted, (data) => this.onQuickReplyDeletedEvent(data))
            .addListener(EventName.quick_reply_updated_send, (data) => this.onQuickReplyUpdatedSendEvent(data))
            .addListener(EventName.quick_reply_updated_display, (data) => this.onQuickReplyUpdatedDisplayEvent(data))
            .addListener(EventName.quick_reply_updated_metadata, (data) => this.onQuickReplyUpdatedMetadataEvent(data));
    }
    onQuickReplyDeletedEvent(data) {
        this.removeQuickReply(data._id);
    }
    onQuickReplyUpdatedSendEvent(data) {
        const qr = this.quickReplies.find((reply) => reply._id === data.quickReply._id);
        if (qr) {
            runInAction(() => {
                qr.send = data.quickReply.send;
            });
        }
    }
    onQuickReplyUpdatedDisplayEvent(data) {
        const qr = this.quickReplies.find((reply) => reply._id === data.quickReply._id);
        if (qr) {
            runInAction(() => {
                qr.display = data.quickReply.display;
            });
        }
    }
    onQuickReplyUpdatedMetadataEvent(data) {
        const qr = this.quickReplies.find((reply) => reply._id === data.quickReply._id);
        if (qr) {
            runInAction(() => {
                qr.metadata = data.quickReply.metadata;
            });
        }
    }
}
__decorate([
    observable
], QuickRepliesStore.prototype, "quickReplies", void 0);
__decorate([
    observable
], QuickRepliesStore.prototype, "total", void 0);
__decorate([
    observable
], QuickRepliesStore.prototype, "quickRepliesLoading", void 0);
__decorate([
    observable
], QuickRepliesStore.prototype, "quickRepliesLoadError", void 0);
__decorate([
    observable
], QuickRepliesStore.prototype, "quickRepliesFetched", void 0);
__decorate([
    observable
], QuickRepliesStore.prototype, "isSettingsModalOpen", void 0);
__decorate([
    observable
], QuickRepliesStore.prototype, "selectedChannel", void 0);
__decorate([
    action
], QuickRepliesStore.prototype, "setSelectedChannel", void 0);
__decorate([
    action
], QuickRepliesStore.prototype, "ensureDefaultChannel", void 0);
__decorate([
    action
], QuickRepliesStore.prototype, "openSettingsModal", void 0);
__decorate([
    action
], QuickRepliesStore.prototype, "closeSettingsModal", void 0);
__decorate([
    action
], QuickRepliesStore.prototype, "upsertQuickReply", void 0);
__decorate([
    action
], QuickRepliesStore.prototype, "removeQuickReply", void 0);
__decorate([
    action
], QuickRepliesStore.prototype, "onQuickReplyCreatedEvent", void 0);
__decorate([
    action
], QuickRepliesStore.prototype, "onQuickReplyDeletedEvent", null);
__decorate([
    action
], QuickRepliesStore.prototype, "onQuickReplyUpdatedSendEvent", null);
__decorate([
    action
], QuickRepliesStore.prototype, "onQuickReplyUpdatedDisplayEvent", null);
__decorate([
    action
], QuickRepliesStore.prototype, "onQuickReplyUpdatedMetadataEvent", null);
export default QuickRepliesStore;
